import { ErrorMessage, Field, Form, Formik } from "formik";
import * as Yup from "yup";
import { Add } from "@material-ui/icons";
import MOModalWrapper from "../../../../components/commons/MOModalWrapper";
import MOButtonV2 from "../../../../components/commons/MOButtonV2";
import FileUploaderDriver from "../../../../components/commons/FileUploadDriver";
import UserIcon from "../../../../assets/icons/auto-driver-user-icon.svg";
import ContactIcon from "../../../../assets/icons/auto-contact-icon.svg";
import mobioApi from "../../../../services";
import { toastMessage } from "../../../../helpers/toastMessage";

const driverDetailSchema = Yup.object().shape({
  firstName: Yup.string()
    .min(3, "First name must be at least 3 characters")
    .max(50, "Too Long!")
    .matches(/^[A-Za-z\s'-]+$/, "Special characters are not allowed")
    .required("First name is required"),
  lastName: Yup.string()
    .min(3, "Last name must be at least 3 characters")
    .max(50, "Too Long!")
    .matches(/^[A-Za-z\s'-]+$/, "Special characters are not allowed")
    .required("Last name is required"),
  phone: Yup.string()
    .required("Phone number is required")
    .matches(/^\+?\d+$/, "Only numbers and '+' prefix allowed")
    .min(11, "Phone number must be at least 11 digits")
    .max(13, "Phone number cannot exceed 13 digits")
    .test(
      "no-special-chars",
      "Special characters are not allowed",
      (value) => !/[^\d+]/.test(value)
    ),
  profileImage: Yup.mixed()
    .nullable()
    .test(
      "file-or-url",
      "Profile image must be a valid URL or file",
      (value) => {
        if (value === null || value === undefined || value === "") return true;
        if (typeof value === "string") {
          try {
            new URL(value);
            return true;
          } catch {
            return false;
          }
        }
        return value instanceof File;
      }
    ),
  brand: Yup.string().min(2, "Brand must be at least 2 characters").required("Brand is required"),
  model: Yup.string().min(4, "Car model must be at least 4 characters").required("Model is required"),
  licensePlateNo: Yup.string()
    .min(2, "Too Short!")
    .max(50, "Too Long")
    .required("License plate number is required"),
  color: Yup.string()
    .min(2, "Too Short!")
    .max(50, "Too Long")
    .required("Color is required"),
});

const emptyValues = {
  firstName: "",
  lastName: "",
  phone: "",
  profileImage: null,
  brand: "",
  model: "",
  licensePlateNo: "",
  color: "",
};

const isValidHttpImageUrl = (url) => {
  if (!url) return true;
  try {
    const parsed = new URL(url);
    return parsed.protocol === "http:" || parsed.protocol === "https:";
  } catch {
    return false;
  }
};

const mapFormToTableRow = (values, imageUrl, id) => ({
  id,
  firstName: values.firstName,
  lastName: values.lastName,
  phone: values.phone,
  profileImage: imageUrl,
  brand: values.brand,
  model: values.model,
  licensePlateNo: values.licensePlateNo,
  color: values.color,
});

const AddEditDriverDetailModal = ({
  token,
  airportId,
  modalOpen,
  setModalOpen,
  singleData,
  onSuccess,
  brandNames,
}) => {
  const isEdit = Boolean(singleData?.id);
  console.log("singleData", singleData);

  const uploadImageToAWS = (file) => {
    return new Promise(async (resolve, reject) => {
      try {
        const formData = new FormData();
        formData.append("image", file);
        const api = mobioApi(token);
        const response = await api.upload(formData);
        resolve(response.data.url);
      } catch (error) {
        toastMessage("error", "Error in uploading profile image of driver");
        reject(error);
      }
    });
  };

  const resolveImageUrl = async (profileImage) => {
   
    if (!profileImage) {
      return null;
    }
    if (profileImage instanceof File) {

      return uploadImageToAWS(profileImage);
    }
    if (typeof profileImage === "string") {
      if (profileImage.startsWith("blob:")) {
        return null;
      }
      if (!isValidHttpImageUrl(profileImage)) {
        throw new Error("Profile image must be a valid http or https URL");
      }
      return profileImage;
    }
    return null;
  };

  const buildApiPayload = async (values) => {
    try {
      const imageUrl = await resolveImageUrl(values.profileImage);
      const payload = {
        firstName: values.firstName,
        lastName: values.lastName,
        phoneNumber: values.phone,
        brandName: values.brand,
        carModel: values.model,
        licensePlateNumber: values.licensePlateNo,
        color: values.color,
        airportId,
        imageUrl,
      };
      if (isEdit) {
        payload.id = singleData.id;
      }
      return { payload, imageUrl };
    } catch (error) {
      console.log("error", error);
      toastMessage("error", "Error in building API payload");
      throw error;
    }
  };

  return (
    <MOModalWrapper
      modalOpen={modalOpen}
      setModalOpen={setModalOpen}
      title={isEdit ? "Edit Driver Detail" : "Add Driver Detail"}
      className={"campaign-add-modal"}
    >
      <div className="p-20">
        <Formik
          enableReinitialize
          initialValues={
            singleData
              ? {
                  firstName: singleData.firstName || "",
                  lastName: singleData.lastName || "",
                  phone: singleData.phoneNumber || "",
                  profileImage: singleData.profileImage ?? null,
                  brand: singleData.brand || "",
                  model: singleData.model || "",
                  licensePlateNo: singleData.licensePlateNo || "",
                  color: singleData.color || "",
                }
              : emptyValues
          }
          validationSchema={driverDetailSchema}
          onSubmit={async (values, { setSubmitting }) => {
            setSubmitting(true);
            try {
              const { payload, imageUrl } = await buildApiPayload(values);
              if(!imageUrl){
              
                throw new Error("Profile image is required");
              }
              const api = mobioApi(token);
              let response = null;
              if (isEdit) {
                response = await api.updateAutoEventDriverDetail(payload);
              } else {
                response = await api.addAutoEventDriverDetail(payload);
              }
              toastMessage("success", response?.data?.msg);
              setModalOpen(false);
              onSuccess(
                mapFormToTableRow(
                  values,
                  imageUrl,
                  isEdit ? singleData.id : response?.data?.data?.id
                )
              );
            } catch (error) {
              console.log("error", error);
              toastMessage(
                "error",
                error?.response?.data?.msg || error?.message || "Request failed"
              );
            } finally {
              setSubmitting(false);
            }
          }}
        >
          {({ values, setFieldValue, isSubmitting }) => (
            <Form>
              <div className="flex gap-32 mt-8">
                <div className="flex-1 min-w-0">
                  <div className="auto-driver-event-driver-detail">
                    <label className="flex align-items-center gap-2 flex-1 cursor-text">
                      <img src={UserIcon} alt="UserIcon" className="shrink-0" />
                      <div className="input_field flex-1 min-w-0">
                        <Field
                          name="firstName"
                          type="text"
                          placeholder="Enter First Name"
                          className="input-field-styles flex-1"
                        />
                      </div>
                    </label>
                  </div>
                  <ErrorMessage name="firstName">
                    {(msg) => (
                      <div style={{ color: "red", margin: "5px" }}>
                        {msg}
                      </div>
                    )}
                  </ErrorMessage>
                </div>
                <div className="flex-1 min-w-0">
                  <div className="auto-driver-event-driver-detail">
                    <label className="flex align-items-center gap-2 flex-1 cursor-text">
                      <img src={UserIcon} alt="UserIcon" className="shrink-0" />
                      <div className="input_field flex-1 min-w-0">
                        <Field
                          name="lastName"
                          type="text"
                          placeholder="Enter Last Name"
                          className="input-field-styles flex-1"
                        />
                      </div>
                    </label>
                  </div>
                  <ErrorMessage name="lastName">
                    {(msg) => (
                      <div style={{ color: "red", margin: "5px" }}>
                        {msg}
                      </div>
                    )}
                  </ErrorMessage>
                </div>
                <div className="flex-1 min-w-0">
                  <div className="auto-driver-event-driver-detail">
                    <label className="flex align-items-center gap-2 flex-1 cursor-text">
                      <img
                        src={ContactIcon}
                        alt="ContactIcon"
                        className="shrink-0"
                      />
                      <div className="input_field flex-1 min-w-0">
                        <Field
                          type="text"
                          maxLength={13}
                          name="phone"
                          placeholder="Enter Phone No"
                          className="input-field-styles flex-1 w-full"
                          onChange={(e) => {
                            const value = e.target.value;
                            if (value === "" || /^\+?\d*$/.test(value)) {
                              setFieldValue("phone", value);
                            }
                          }}
                        />
                      </div>
                    </label>
                  </div>
                  <ErrorMessage name="phone">
                    {(msg) => (
                      <div style={{ color: "red", margin: "5px" }}>
                        {msg}
                      </div>
                    )}
                  </ErrorMessage>
                </div>
              </div>

              <div className="box-shadow-all-3 auto-driver-field-spacing-spacing mt-24">
                <div className="flex gap-24">
                  <div className="flex-1">
                    <div className="input_field">
                      <p className="text-gray font-16 font-500 mb-12">Brand</p>
                      <Field
                        as="select"
                        className="border-gray-color optionStyle custom-select"
                        name="brand"
                        style={{ border: "1px solid #878787 !important" }}
                      >
                        <option value="">Select Brand</option>
                        {brandNames?.map((brandName) => (
                          <option key={brandName} value={brandName}>
                            {brandName}
                          </option>
                        ))}
                      </Field>
                    </div>
                    <ErrorMessage name="brand">
                      {(msg) => (
                        <div style={{ color: "red", margin: "5px" }}>
                          {msg}
                        </div>
                      )}
                    </ErrorMessage>
                  </div>
                  <div className="flex-1">
                    <div className="input_field">
                      <p className="text-gray font-16 font-500 mb-12">
                        Car Model
                      </p>
                      <Field
                        as="select"
                        className="border-gray-color optionStyle custom-select-car"
                        name="model"
                        style={{ border: "1px solid #878787 !important" }}
                      >
                        <option value="">Select Year</option>
                        {Array.from(
                          { length: new Date().getFullYear() - 2000 + 1 },
                          (_, i) => 2000 + i
                        )
                          .reverse()
                          .map((year) => (
                            <option key={year} value={year.toString()}>
                              {year}
                            </option>
                          ))}
                      </Field>
                    </div>
                    <ErrorMessage name="model">
                      {(msg) => (
                        <div style={{ color: "red", margin: "5px" }}>
                          {msg}
                        </div>
                      )}
                    </ErrorMessage>
                  </div>
                </div>
                <div className="flex gap-24 mt-16">
                  <div className="flex-1">
                    <div className="input_field">
                      <p className="text-gray font-16 font-500 mb-12">
                        License Plate Number
                      </p>
                      <Field
                        type="text"
                        name="licensePlateNo"
                        className="input-field-styles"
                        placeholder="Enter Plate Number"
                      />
                    </div>
                    <ErrorMessage name="licensePlateNo">
                      {(msg) => (
                        <div style={{ color: "red", margin: "5px" }}>
                          {msg}
                        </div>
                      )}
                    </ErrorMessage>
                  </div>
                  <div className="flex-1">
                    <div className="input_field">
                      <p className="text-gray font-16 font-500 mb-12">Color</p>
                      <Field
                        type="text"
                        name="color"
                        placeholder="Enter Color"
                        className="input-field-styles"
                      />
                    </div>
                    <ErrorMessage name="color">
                      {(msg) => (
                        <div style={{ color: "red", margin: "5px" }}>
                          {msg}
                        </div>
                      )}
                    </ErrorMessage>
                  </div>
                </div>
              </div>

              <div className="flex align-item-center gap-16 mt-24">
                <Add style={{ height: "36px", opacity: 0.8 }} />
                <p className="font-14 font-500 m-0">Add Profile Picture</p>
              </div>
              <FileUploaderDriver
                accept={".png, .jpg, .jpeg"}
                label={"Upload Image"}
                callback={(files) => {
                  if (files && files.length > 0) {
                    setFieldValue("profileImage", files[0]);
                  } else {
                    setFieldValue("profileImage", null);
                  }
                }}
                style={{ position: "relative" }}
                profileImage={values.profileImage}
              />

              <div className="flex align-item-center justify-content-end mt-32 gap-16">
         
                  <MOButtonV2
                    text="Cancel"
                    textColor="#1D2433"
                    radius={7}
                    backgroundColor="#ffffff"
                    border="1px solid #E0E0E0"
                    fontSize={12}
                    height={38}
                    onClick={() => setModalOpen(false)}
                  />
          
                <MOButtonV2
                  type="submit"
                  text={isEdit ? "UPDATE" : "ADD"}
                  textColor="#FCFCFD"
                  radius={7}
                  backgroundColor="#2CA9DD"
                  fontSize={12}
                  height={38}
                  disabled={isSubmitting}
                  loading={isSubmitting}
                />
              </div>
            </Form>
          )}
        </Formik>
      </div>
    </MOModalWrapper>
  );
};

export default AddEditDriverDetailModal;
